import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import {
    Card,
    Row,
    Col,
    Input,
    Select,
    Radio,
    Calendar,
    Button,
    Tag,
    Icon,
    Tooltip,
    Alert,
    Empty,
    Spin,
    Modal,
    message,
    notification
} from "antd";
import {
    scheduleTeamsCall,
    fetchTeamsCalls,
    fetchTeamsCallDetail,
    cancelTeamsCall,
    teamsCallsPage,
    normalizeCallStatus,
    callStatusLabel
} from "services/telephony-service";
import { canViewCallSchedule } from "constants/user-visibility";
import {
    DAY_START_HOUR,
    DAY_END_HOUR,
    SLOT_MINUTES,
    MAX_MONTHS_AHEAD,
    BLOCKING_STATUSES,
    DURATION_OPTIONS,
    CALL_STATUS_COLORS,
    SPEAKER_COLORS
} from "constants/teams-calls";
import {
    isEmail,
    initialsOf,
    intervalsOverlap,
    nextSlot
} from "components/helper";
import { parseTranscript, groupCues } from "utilities/transcript-utils";
import "styles/call-schedule.css";

const { Option } = Select;

/**
 * Call Schedule — a self-contained card to schedule a Microsoft Teams call for a
 * case (real Outlook event + WhatsApp/email invite) and see the case's calls.
 *
 * Portable: drop `<CallScheduleCard caseId={id} />` (optionally `companyId`)
 * anywhere — it reads the organizer email from redux itself.
 *
 * Availability: chitthi has no free/busy endpoint, so "blocked" times are this
 * case's own upcoming calls; overlapping one disables scheduling.
 */
class CallScheduleCard extends React.Component {
    constructor(props) {
        super(props);
        const start = nextSlot(SLOT_MINUTES);
        this.state = {
            subject: "",
            attendeeEmails: [],
            callType: "case",
            date: start.clone().startOf("day"),
            startTime: start,
            durationMins: 30,

            calls: [],
            listLoading: true,
            scheduling: false,
            
            // In-app viewer for a call's recording / transcript —
            // { call, type: "recording" | "transcript" } or null.
            mediaModal: null,

            // Accordion state (only meaningful when `collapsible` is set).
            collapsed: Boolean(props.defaultCollapsed)
        };
    }

    toggle = () => this.setState(prev => ({ collapsed: !prev.collapsed }));

    // Visible to the Credit Manager group (7) — same rule as the "My Calls"
    // header menu (see canViewCallSchedule).
    isAllowed = () => canViewCallSchedule(this.props.user);

    componentDidMount() {
        if (!this.isAllowed()) {
            return;
        }
        this.loadCalls();
        this.ensureValidStart();
    }

    loadCalls = () => {
        const { caseId } = this.props;
        if (!caseId) {
            this.setState({ calls: [], listLoading: false });
            return;
        }
        this.setState({ listLoading: true });
        fetchTeamsCalls({ caseId })
            .then(({ data }) => {
                const { results } = teamsCallsPage(data);
                // status now arrives as an integer code — normalise to the string
                // key so the rest of the component's status logic works unchanged.
                const calls = results.map(c => ({
                    ...c,
                    status: normalizeCallStatus(c.status)
                }));
                // New blocks may invalidate the current pick — re-check after load.
                this.setState(
                    { calls, listLoading: false },
                    this.ensureValidStart
                );
            })
            .catch(() =>
                this.setState({ calls: [], listLoading: false }, this.ensureValidStart)
            );
    };

    // Upcoming/live calls as moment intervals — the source of "blocked" time.
    blockingIntervals = () =>
        (this.state.calls || [])
            .filter(c => BLOCKING_STATUSES.includes(c.status))
            .map(c => ({
                start: moment(c.scheduled_start),
                end: moment(c.scheduled_end),
                subject: c.subject,
                status: c.status
            }))
            .filter(i => i.start.isValid() && i.end.isValid());

    // The slot the form currently describes, or null if incomplete.
    proposedInterval = () => {
        const { date, startTime, durationMins } = this.state;
        if (!date || !startTime || !durationMins) {
            return null;
        }
        const start = date
            .clone()
            .hour(startTime.hour())
            .minute(startTime.minute())
            .second(0)
            .millisecond(0);
        const end = start.clone().add(durationMins, "minutes");
        return { start, end };
    };

    // First blocking interval the proposed slot collides with, if any.
    conflictingCall = () => {
        const proposed = this.proposedInterval();
        if (!proposed) {
            return null;
        }
        return (
            this.blockingIntervals().find(i =>
                intervalsOverlap(proposed.start, proposed.end, i.start, i.end)
            ) || null
        );
    };

    setField = key => value => this.setState({ [key]: value });

    // Additional-attendee tags must be valid emails — keep the valid ones and
    // reject the rest with a message (trims + de-dupes).
    onAttendeesChange = values => {
        const valid = [];
        const invalid = [];
        (values || []).forEach(v => {
            const e = String(v).trim();
            if (!e) return;
            if (isEmail(e)) {
                if (!valid.includes(e)) valid.push(e);
            } else {
                invalid.push(e);
            }
        });
        if (invalid.length) {
            message.error(`Not a valid email: ${invalid.join(", ")}`);
        }
        this.setState({ attendeeEmails: valid });
    };

    // Half-hour slots for the selected day, tagged free/blocked/selected.
    daySlots = () => {
        const { date } = this.state;
        if (!date) {
            return [];
        }
        const blocks = this.blockingIntervals();
        const proposed = this.proposedInterval();
        const base = date
            .clone()
            .hour(DAY_START_HOUR)
            .minute(0)
            .second(0)
            .millisecond(0);
        const count = ((DAY_END_HOUR - DAY_START_HOUR) * 60) / SLOT_MINUTES;
        const slots = [];
        for (let i = 0; i < count; i += 1) {
            const slotStart = base.clone().add(i * SLOT_MINUTES, "minutes");
            const slotEnd = slotStart.clone().add(SLOT_MINUTES, "minutes");
            const block = blocks.find(b =>
                intervalsOverlap(slotStart, slotEnd, b.start, b.end)
            );
            const selected =
                proposed &&
                intervalsOverlap(slotStart, slotEnd, proposed.start, proposed.end);
            const past = slotStart.isBefore(moment());
            slots.push({ slotStart, slotEnd, block, selected, past });
        }
        return slots;
    };

    pickSlot = slotStart => this.setState({ startTime: slotStart.clone() });

    // Free, non-past 30-min start options for the selected date. Blocked and
    // past slots are excluded, so they can never be picked from the dropdown.
    availableStartSlots = () =>
        this.daySlots()
            .filter(s => !s.block && !s.past)
            .map(s => ({
                m: s.slotStart,
                key: s.slotStart.format("HH:mm"),
                label: s.slotStart.format("h:mm A")
            }));

    // Keep the selected start valid for the current date/availability. When it
    // is no longer a free future slot (e.g. after switching from tomorrow to
    // today, where 9:00 AM is now in the past), snap to the first free slot —
    // or clear it when the day has none.
    ensureValidStart = () => {
        const opts = this.availableStartSlots();
        const cur = this.state.startTime;
        const ok = cur && opts.some(o => o.key === cur.format("HH:mm"));
        if (!ok) {
            this.setState({ startTime: opts.length ? opts[0].m.clone() : null });
        }
    };

    setDate = d =>
        this.setState({ date: d.clone().startOf("day") }, this.ensureValidStart);

    onStartSelect = key => {
        const parts = key.split(":");
        const t = this.state.date
            .clone()
            .hour(Number(parts[0]))
            .minute(Number(parts[1]))
            .second(0)
            .millisecond(0);
        this.setState({ startTime: t });
    };

    validate = () => {
        const { subject, date, startTime, durationMins } = this.state;
        const { organizerEmail, recipientPhone, recipientEmail } = this.props;

        if (!organizerEmail) {
            return "Your account email is unavailable, so the call organizer can't be set.";
        }
        if (!subject || !subject.trim()) {
            return "Enter a subject for the call.";
        }
        if (!recipientPhone && !recipientEmail) {
            return "The main borrower has no phone or email on file. Add one in the Applicants section.";
        }
        if (!date || !startTime || !durationMins) {
            return "Pick a date, start time and duration.";
        }
        const proposed = this.proposedInterval();
        if (proposed.start.isBefore(moment())) {
            return "Choose a start time in the future.";
        }
        if (
            proposed.start.isAfter(
                moment().add(MAX_MONTHS_AHEAD, "months").endOf("day")
            )
        ) {
            return "Calls can be scheduled up to 3 months in advance.";
        }
        if (this.conflictingCall()) {
            return "This slot overlaps a call already scheduled for this case. Pick a free slot.";
        }
        return null;
    };

    handleSchedule = () => {
        const error = this.validate();
        if (error) {
            message.error(error);
            return;
        }
        const { subject, attendeeEmails } = this.state;
        const {
            organizerEmail,
            userId,
            userName,
            caseId,
            companyId,
            recipientName,
            recipientPhone,
            recipientEmail
        } = this.props;
        const proposed = this.proposedInterval();

        // Customer (main borrower) contact — sent as invite_email / invite_mobile.
        const inviteEmail = recipientEmail ? String(recipientEmail).trim() : undefined;
        const inviteMobile = String(recipientPhone || "").replace(/\D/g, "") || undefined;
        // Default attendee: the login user, plus any the user added (deduped).
        const attendees = Array.from(
            new Set(
                [
                    ...(organizerEmail ? [organizerEmail] : []),
                    ...(attendeeEmails || [])
                ].filter(Boolean)
            )
        );

        // start/end are local ISO8601 with offset (e.g. 2026-07-27T11:41:03+05:30).
        // organizer_email is set server-side (static mailbox), so we don't send it;
        // the booking user goes in user_id / user_email / user_name.
        const payload = {
            subject: subject.trim(),
            start_time: proposed.start.format(),
            end_time: proposed.end.format(),
            case_id: caseId != null ? String(caseId) : null,
            company_id: companyId != null ? String(companyId) : null,
            user_id: userId != null ? userId : undefined,
            user_email: organizerEmail,
            user_name: userName || undefined,
            invite_email: inviteEmail,
            invite_mobile: inviteMobile,
            source: "LOS",
            attendee_emails: attendees
        };

        this.setState({ scheduling: true });
        scheduleTeamsCall(payload)
            .then(res => {
                const body = (res && res.data) || {};
                const info =
                    body.data && typeof body.data === "object" ? body.data : body;
                const joinUrl = info.join_url;
                notification.success({
                    message: "Call scheduled",
                    description: (
                        <span>
                            {`Invite sent to ${recipientName ||
                                inviteEmail ||
                                inviteMobile ||
                                "the borrower"}.`}
                            {joinUrl ? (
                                <div style={{ marginTop: 6 }}>
                                    <a
                                        href={joinUrl}
                                        target={"_blank"}
                                        rel={"noopener noreferrer"}
                                    >
                                        Open meeting link
                                    </a>
                                </div>
                            ) : null}
                        </span>
                    ),
                    duration: 6
                });
                this.setState({ scheduling: false, subject: "" });
                this.loadCalls();
            })
            .catch(err => {
                // The shared axios instance rejects with the response body (not
                // the axios error), so accept both shapes.
                const body = (err && err.response && err.response.data) || err || {};
                const msg =
                    (body && (body.error || body.message || body.detail)) ||
                    "Could not schedule the call. Please try again.";
                notification.error({ message: "Scheduling failed", description: msg });
                this.setState({ scheduling: false });
            });
    };

    handleJoin = call => {
        // The list already carries join_url — prefer it; fall back to detail.
        if (call.join_url) {
            window.open(call.join_url, "_blank", "noopener,noreferrer");
            return;
        }
        if (!call.meeting_id) {
            message.info("Meeting link is not ready yet.");
            return;
        }
        const hide = message.loading("Opening meeting…", 0);
        fetchTeamsCallDetail(call.meeting_id)
            .then(({ data }) => {
                hide();
                const d = (data && data.data) || data || {};
                if (d.join_url) {
                    window.open(d.join_url, "_blank", "noopener,noreferrer");
                } else {
                    message.error("Meeting link unavailable.");
                }
            })
            .catch(() => {
                hide();
                message.error("Could not open the meeting link.");
            });
    };

    openMedia = (call, type) => this.setState({ mediaModal: { call, type } });

    closeMedia = () => this.setState({ mediaModal: null });

    handleCancel = call => {
        if (!call.meeting_id) {
            message.error("This call can't be cancelled (no meeting id).");
            return;
        }
        Modal.confirm({
            title: "Cancel this call?",
            content: `"${call.subject}" — attendees will be notified.`,
            okText: "Cancel call",
            okType: "danger",
            cancelText: "Keep",
            onOk: () =>
                cancelTeamsCall(call.meeting_id)
                    .then(() => {
                        message.success("Call cancelled.");
                        this.loadCalls();
                    })
                    .catch(() => message.error("Could not cancel the call."))
        });
    };

    renderDateQuickChips() {
        const { date } = this.state;
        const chips = [
            { label: "Today", m: moment().startOf("day") },
            { label: "Tomorrow", m: moment().add(1, "day").startOf("day") },
            { label: moment().add(2, "day").format("ddd"), m: moment().add(2, "day").startOf("day") }
        ];
        return (
            <div style={{ marginBottom: 10 }}>
                {chips.map(c => {
                    const active = date && date.isSame(c.m, "day");
                    return (
                        <span
                            key={c.label}
                            className={"cs-chip" + (active ? " cs-chip--active" : "")}
                            onClick={() => this.setDate(c.m)}
                        >
                            {c.label}
                        </span>
                    );
                })}
            </div>
        );
    }

    // Step the calendar one month, clamped to [today, +MAX_MONTHS_AHEAD].
    navMonth = (fromValue, delta) => {
        const target = fromValue.clone().add(delta, "month").startOf("day");
        const min = moment().startOf("day");
        const max = moment().add(MAX_MONTHS_AHEAD, "months").startOf("day");
        const d = target.isBefore(min) ? min : target.isAfter(max) ? max : target;
        this.setDate(d);
    };

    // Compact month navigator that replaces antd's default year/mode header —
    // there's no reason to pick a year inside a 3-month window.
    renderCalendarHeader = ({ value }) => {
        const canPrev = value.isAfter(moment(), "month");
        const canNext = value.isBefore(
            moment().add(MAX_MONTHS_AHEAD, "months"),
            "month"
        );
        return (
            <div className={"cs-cal-nav"}>
                <button
                    type={"button"}
                    className={"cs-cal-nav__btn"}
                    disabled={!canPrev}
                    aria-label={"Previous month"}
                    onClick={() => this.navMonth(value, -1)}
                >
                    <Icon type={"left"} />
                </button>
                <span className={"cs-cal-nav__label"}>
                    {value.format("MMMM YYYY")}
                </span>
                <button
                    type={"button"}
                    className={"cs-cal-nav__btn"}
                    disabled={!canNext}
                    aria-label={"Next month"}
                    onClick={() => this.navMonth(value, 1)}
                >
                    <Icon type={"right"} />
                </button>
            </div>
        );
    };

    renderForm() {
        const {
            subject,
            attendeeEmails,
            startTime,
            durationMins,
            scheduling
        } = this.state;
        const {
            organizerEmail,
            recipientName,
            recipientPhone,
            recipientEmail
        } = this.props;
        const conflict = this.conflictingCall();
        const proposed = this.proposedInterval();
        const initial =
            (organizerEmail && organizerEmail.charAt(0).toUpperCase()) || "?";
        const hasRecipient = Boolean(recipientName || recipientPhone || recipientEmail);
        const recipientInitial =
            (recipientName && recipientName.charAt(0).toUpperCase()) || "?";

        return (
            <div className={"cs-panel"}>
                <div className={"cs-panel__title"}>
                    <Icon type={"form"} style={{ marginRight: 6 }} /> Call details
                </div>

                <div style={{ marginBottom: 12 }}>
                    <div className={"cs-label"}>Subject</div>
                    <Input
                        value={subject}
                        size={"large"}
                        placeholder={"e.g. TVR discussion with borrower"}
                        onChange={e => this.setState({ subject: e.target.value })}
                    />
                </div>

                <div className={"cs-recipient"}>
                    <div className={"cs-recipient__head"}>
                        <Icon type={"user"} style={{ marginRight: 5 }} />
                        Recipient · Main borrower
                    </div>
                    {hasRecipient ? (
                        <div className={"cs-recipient__body"}>
                            <span className={"cs-recipient__avatar"}>
                                {recipientInitial}
                            </span>
                            <div style={{ minWidth: 0 }}>
                                <div className={"cs-recipient__name"}>
                                    {recipientName || "Unnamed borrower"}
                                </div>
                                <div className={"cs-recipient__contacts"}>
                                    <span>
                                        <Icon type={"phone"} />
                                        {recipientPhone || "No phone on file"}
                                    </span>
                                    <span>
                                        <Icon type={"mail"} />
                                        {recipientEmail || "No email on file"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={"cs-recipient__empty"}>
                            <Icon type={"exclamation-circle"} style={{ marginRight: 5 }} />
                            No main borrower marked for this case. Set one in the Applicants
                            section to schedule a call.
                        </div>
                    )}
                </div>

                <div style={{ marginBottom: 12 }}>
                    <div className={"cs-label"}>Additional attendee emails (optional)</div>
                    <Select
                        mode={"tags"}
                        value={attendeeEmails}
                        onChange={this.onAttendeesChange}
                        tokenSeparators={[",", " "]}
                        placeholder={"name@company.com"}
                        style={{ width: "100%" }}
                        dropdownStyle={{ display: "none" }}
                    />
                </div>

                <Row gutter={12}>
                    <Col xs={24} sm={10} style={{ marginBottom: 12 }}>
                        <div className={"cs-label"}>Start time</div>
                        <Select
                            value={startTime ? startTime.format("HH:mm") : undefined}
                            placeholder={"Select a free slot"}
                            style={{ width: "100%" }}
                            onChange={this.onStartSelect}
                            notFoundContent={"No free slots on this day"}
                        >
                            {this.availableStartSlots().map(o => (
                                <Option key={o.key} value={o.key}>
                                    {o.label}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={14} style={{ marginBottom: 4 }}>
                        <div className={"cs-label"}>Duration</div>
                        <Radio.Group
                            value={durationMins}
                            onChange={e => this.setState({ durationMins: e.target.value })}
                            size={"default"}
                        >
                            {DURATION_OPTIONS.map(d => (
                                <Radio.Button key={d} value={d}>
                                    {d}m
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    </Col>
                </Row>

                <div className={"cs-organizer"}>
                    <span className={"cs-avatar"}>{initial}</span>
                    <span>
                        Organizer <strong>{organizerEmail || "unknown (log in again)"}</strong>
                    </span>
                </div>

                {conflict ? (
                    <Alert
                        style={{ marginTop: 12, borderRadius: 10 }}
                        type={"warning"}
                        showIcon
                        message={"Time slot busy"}
                        description={`Overlaps "${conflict.subject}" (${moment(
                            conflict.start
                        ).format("h:mm A")}–${moment(conflict.end).format("h:mm A")}).`}
                    />
                ) : proposed ? (
                    <div
                        style={{
                            marginTop: 12,
                            fontSize: 12,
                            color: "#4b5563",
                            background: "#eef3ff",
                            border: "1px solid #dbe4fb",
                            borderRadius: 10,
                            padding: "9px 12px"
                        }}
                    >
                        <Icon type={"clock-circle"} style={{ marginRight: 6, color: "#2c5fe0" }} />
                        {proposed.start.format("ddd, DD MMM · h:mm A")} – {proposed.end.format("h:mm A")}
                        <span style={{ color: "#9ca3af" }}>{`  (${durationMins} min)`}</span>
                    </div>
                ) : null}

                <Button
                    type={"primary"}
                    className={"cs-cta"}
                    icon={"video-camera"}
                    loading={scheduling}
                    disabled={Boolean(conflict)}
                    onClick={this.handleSchedule}
                    block
                    style={{ marginTop: 14 }}
                >
                    Schedule call
                </Button>
            </div>
        );
    }

    renderAvailability() {
        const { date } = this.state;
        const slots = this.daySlots();
        return (
            <div className={"cs-panel"}>
                <div className={"cs-panel__title"}>
                    <Icon type={"calendar"} style={{ marginRight: 6 }} /> Pick date &amp; time
                </div>

                {this.renderDateQuickChips()}

                <div className={"cs-cal"}>
                    <Calendar
                        fullscreen={false}
                        value={date}
                        onChange={this.setDate}
                        headerRender={this.renderCalendarHeader}
                        disabledDate={current =>
                            current &&
                            (current.isBefore(moment().startOf("day")) ||
                                current.isAfter(
                                    moment().add(MAX_MONTHS_AHEAD, "months").endOf("day")
                                ))
                        }
                    />
                </div>

                <div className={"cs-legend"}>
                    <span><i style={{ background: "#ecfdf3", border: "1px solid #d1fadf" }} /> Free</span>
                    <span><i style={{ background: "#fdeceb", border: "1px solid #f6bcb6", boxShadow: "inset 3px 0 0 #d0342c" }} /> Busy</span>
                    <span><i style={{ background: "linear-gradient(135deg,#2c5fe0,#1e46b8)" }} /> Selected</span>
                </div>

                <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", margin: "6px 2px 8px" }}>
                    {date ? date.format("dddd, DD MMM YYYY") : "—"}
                </div>

                <div className={"cs-slotgrid"}>
                    {slots.map(({ slotStart, slotEnd, block, selected, past }) => {
                        const cls = selected
                            ? "cs-slot cs-slot--selected"
                            : block
                                ? "cs-slot cs-slot--blocked"
                                : "cs-slot cs-slot--free";
                        const disabled = Boolean(block) || past;
                        const node = (
                            <div
                                key={slotStart.valueOf()}
                                className={cls}
                                style={past && !block ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                                onClick={() => (disabled ? null : this.pickSlot(slotStart))}
                            >
                                {slotStart.format("h:mm")}
                                <span className={"cs-slot__sub"}>
                                    {block ? (
                                        <span>
                                            <Icon type={"lock"} style={{ fontSize: 9, marginRight: 2 }} />
                                            busy
                                        </span>
                                    ) : (
                                        slotStart.format("A")
                                    )}
                                </span>
                            </div>
                        );
                        return block ? (
                            <Tooltip
                                key={slotStart.valueOf()}
                                title={`${block.subject || "Busy"} · ${slotStart.format(
                                    "h:mm A"
                                )}–${slotEnd.format("h:mm A")}`}
                            >
                                {node}
                            </Tooltip>
                        ) : (
                            node
                        );
                    })}
                </div>
            </div>
        );
    }

    renderScheduledList() {
        const { calls, listLoading } = this.state;
        if (listLoading) {
            return (
                <div style={{ textAlign: "center", padding: 20 }}>
                    <Spin />
                </div>
            );
        }
        if (!calls.length) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={"No calls scheduled for this case yet"}
                />
            );
        }
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {calls.map(call => {
                    const start = moment(call.scheduled_start);
                    const end = moment(call.scheduled_end);
                    const when = start.isValid()
                        ? `${start.format("DD MMM, h:mm A")}${
                            end.isValid() ? `–${end.format("h:mm A")}` : ""
                        }`
                        : "—";
                    const canJoin = BLOCKING_STATUSES.includes(call.status);
                    const canCancel = call.status === "scheduled";
                    return (
                        <div key={call.id} className={"cs-callitem"}>
                            <div
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 10,
                                    flex: "0 0 38px",
                                    background: "#eaf1ff",
                                    color: "#2c5fe0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                <Icon type={"phone"} theme={"filled"} />
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        fontWeight: 600,
                                        fontSize: 13,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {call.subject || "Untitled call"}
                                </div>
                                <div style={{ fontSize: 12, color: "#8891a5", marginTop: 2 }}>
                                    <Icon type={"clock-circle"} style={{ marginRight: 4 }} />
                                    {when}
                                </div>
                                {call.user_name || call.user_email ? (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "#8891a5",
                                            marginTop: 2,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        <Icon type={"user"} style={{ marginRight: 4 }} />
                                        {call.user_name || "—"}
                                        {call.user_email ? ` · ${call.user_email}` : ""}
                                    </div>
                                ) : null}
                                {call.cancelled_by ? (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "#cf1322",
                                            marginTop: 2,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        <Icon type={"stop"} style={{ marginRight: 4 }} />
                                        Cancelled by{" "}
                                        {call.cancelled_by.user_name ||
                                            call.cancelled_by.user_email ||
                                            "—"}
                                        {call.cancelled_by.cancelled_at
                                            ? ` · ${moment(
                                                call.cancelled_by.cancelled_at
                                            ).format("DD MMM, h:mm A")}`
                                            : ""}
                                    </div>
                                ) : null}
                            </div>
                            <Tag color={CALL_STATUS_COLORS[call.status] || "default"} style={{ borderRadius: 6 }}>
                                {callStatusLabel(call.status)}
                            </Tag>
                            <div className={"cs-actions"} style={{ whiteSpace: "nowrap" }}>
                                {canJoin ? (
                                    <Button size={"small"} type={"link"} onClick={() => this.handleJoin(call)}>
                                        <Icon type={"video-camera"} /> Join
                                    </Button>
                                ) : null}
                                {call.recording_url ? (
                                    <Button
                                        size={"small"}
                                        type={"link"}
                                        onClick={() => this.openMedia(call, "recording")}
                                    >
                                        <Icon type={"play-circle"} /> Recording
                                    </Button>
                                ) : null}
                                {call.transcription_text ? (
                                    <Button
                                        size={"small"}
                                        type={"link"}
                                        onClick={() => this.openMedia(call, "transcript")}
                                    >
                                        <Icon type={"file-text"} /> Transcript
                                    </Button>
                                ) : null}
                                {canCancel ? (
                                    <Button
                                        size={"small"}
                                        type={"link"}
                                        onClick={() => this.handleCancel(call)}
                                        style={{ color: "#cf1322" }}
                                    >
                                        Cancel
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderTranscriptChat(call) {
        const cues = parseTranscript(call.transcription_text);
        if (!cues.length) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={"Transcript is empty"}
                />
            );
        }
        const groups = groupCues(cues);
        // Distinct speakers in order of first appearance → stable colors.
        const speakers = [];
        cues.forEach(c => {
            const s = c.speaker || "Participant";
            if (!speakers.includes(s)) {
                speakers.push(s);
            }
        });
        // The call owner's lines sit on the right, like their own chat app.
        const owner = String(call.user_name || "").trim().toLowerCase();
        const isSelf = name =>
            Boolean(owner) && String(name).trim().toLowerCase() === owner;
        const start = moment(call.scheduled_start);
        const meta = [
            start.isValid() ? start.format("DD MMM YYYY · h:mm A") : null,
            `${speakers.length} participant${speakers.length === 1 ? "" : "s"}`
        ]
            .filter(Boolean)
            .join("  ·  ");

        return (
            <div className={"cs-chat"}>
                <div className={"cs-chat__meta"}>
                    <span>
                        <Icon type={"message"} />
                        {meta}
                    </span>
                </div>
                {groups.map((g, gi) => {
                    const name = g.speaker || "Participant";
                    const colors =
                        SPEAKER_COLORS[speakers.indexOf(name) % SPEAKER_COLORS.length];
                    const self = isSelf(name);
                    return (
                        <div
                            key={gi}
                            className={
                                "cs-chat__group" + (self ? " cs-chat__group--self" : "")
                            }
                        >
                            <span
                                className={"cs-chat__avatar"}
                                style={{
                                    background: self
                                        ? "linear-gradient(135deg,#2c5fe0,#1e46b8)"
                                        : `linear-gradient(135deg,${colors[0]},${colors[1]})`
                                }}
                            >
                                {initialsOf(name)}
                            </span>
                            <div className={"cs-chat__body"}>
                                <div
                                    className={"cs-chat__name"}
                                    style={{ color: self ? "#2c5fe0" : colors[0] }}
                                >
                                    {name}
                                    {g.cues[0].time ? <small>{g.cues[0].time}</small> : null}
                                </div>
                                {g.cues.map((cue, ci) => (
                                    <div key={ci}>
                                        <div className={"cs-chat__bubble"}>
                                            {cue.text}
                                            {cue.time ? (
                                                <span className={"cs-chat__stamp"}>
                                                    {cue.time}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderMediaModal() {
        const { mediaModal } = this.state;
        if (!mediaModal) {
            return null;
        }
        const { call, type } = mediaModal;
        const isRecording = type === "recording";
        return (
            <Modal
                visible
                title={
                    <span>
                        <Icon
                            type={isRecording ? "play-circle" : "message"}
                            style={{ marginRight: 8, color: "#2c5fe0" }}
                        />
                        {`${call.subject || "Call"} · ${
                            isRecording ? "Recording" : "Transcript"
                        }`}
                    </span>
                }
                footer={null}
                width={isRecording ? 760 : 680}
                destroyOnClose
                onCancel={this.closeMedia}
            >
                {isRecording ? (
                    <video
                        src={call.recording_url}
                        controls
                        controlsList={"nodownload"}
                        style={{
                            width: "100%",
                            maxHeight: "62vh",
                            borderRadius: 10,
                            background: "#000",
                            outline: "none"
                        }}
                    >
                        Your browser cannot play this recording.
                    </video>
                ) : (
                    this.renderTranscriptChat(call)
                )}
            </Modal>
        );
    }

    render() {
        // Gated to the Credit Manager group (see canViewCallSchedule).
        if (!this.isAllowed()) {
            return null;
        }
        const { collapsible } = this.props;
        const collapsed = collapsible && this.state.collapsed;
        const upcoming = (this.state.calls || []).filter(c =>
            BLOCKING_STATUSES.includes(c.status)
        ).length;
        return (
            <div className={"cs-schedule"}>
                <Card
                    bordered={false}
                    bodyStyle={{ padding: 18 }}
                    style={{
                        borderRadius: 16,
                        boxShadow: "0 6px 24px -12px rgba(16,24,40,.18)"
                    }}
                >
                    <div
                        className={"cs-hero" + (collapsible ? " cs-hero--toggle" : "")}
                        onClick={collapsible ? this.toggle : undefined}
                    >
                        <div className={"cs-hero__left"}>
                            <span className={"cs-hero__icon"}>
                                <Icon type={"phone"} theme={"filled"} />
                            </span>
                            <div>
                                <div className={"cs-hero__title"}>Schedule a Call</div>
                                <div className={"cs-hero__sub"}>
                                    Book a Microsoft Teams call and notify the borrower over WhatsApp or email
                                </div>
                            </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span
                                className={"cs-hero__badge"}
                                onClick={e => {
                                    e.stopPropagation();
                                    this.loadCalls();
                                }}
                            >
                                {this.state.listLoading ? (
                                    <Icon type={"loading"} />
                                ) : (
                                    <Icon type={"reload"} />
                                )}
                                {`${upcoming} upcoming`}
                            </span>
                            {collapsible ? (
                                <Icon
                                    className={"cs-hero__chev"}
                                    type={collapsed ? "down" : "up"}
                                />
                            ) : null}
                        </div>
                    </div>

                    {collapsed ? null : (
                        <React.Fragment>
                            <Row gutter={18} style={{ marginTop: 16 }}>
                                <Col xs={24} lg={13} style={{ marginBottom: 16 }}>
                                    {this.renderForm()}
                                </Col>
                                <Col xs={24} lg={11} style={{ marginBottom: 16 }}>
                                    {this.renderAvailability()}
                                </Col>
                            </Row>

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    margin: "6px 2px 12px"
                                }}
                            >
                                <span style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>
                                    Scheduled calls
                                </span>
                                <span style={{ marginLeft: 8, fontSize: 12, color: "#8891a5" }}>
                                    {`(${(this.state.calls || []).length})`}
                                </span>
                            </div>
                            {this.renderScheduledList()}
                        </React.Fragment>
                    )}
                </Card>
                {this.renderMediaModal()}
            </div>
        );
    }
}

CallScheduleCard.propTypes = {
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    organizerEmail: PropTypes.string,
    // Logged-in LOS user — sent as user_id / user_name on the schedule payload.
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userName: PropTypes.string,
    // Whole user slice — needed for the group-based visibility check.
    user: PropTypes.object,
    // Main-borrower contact — shown read-only for verification and used as the
    // notification recipient (WhatsApp to phone, else email).
    recipientName: PropTypes.string,
    recipientPhone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recipientEmail: PropTypes.string,
    // When true the hero header acts as an accordion toggle for the body.
    collapsible: PropTypes.bool,
    defaultCollapsed: PropTypes.bool
};

const mapStateToProps = state => {
    const data = state.user.getIn(["data"]);
    const get = k => (data && data.get ? data.get(k) : undefined);
    const fullName =
        get("full_name") ||
        [get("first_name"), get("last_name")]
            .filter(Boolean)
            .join(" ")
            .trim() ||
        get("email") ||
        null;
    return {
        organizerEmail: get("email"),
        userId: get("id"),
        userName: fullName,
        user: state.user
    };
};

export default connect(mapStateToProps)(CallScheduleCard);
